// src/components/events/TestingAttendeesPrintButton.js
import React, { useMemo } from "react";
import { Button } from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { gradArray } from "../form/fields/inputFields";

// Helpers
const gradLabel = (degree) =>
  gradArray.find((g) => g.value === Number(degree))?.label ||
  String(degree ?? "");

const tableStyle = {
  headStyles: { fillColor: [255, 255, 255], textColor: 0 },
  styles: { fontSize: 9, cellPadding: 4, valign: "middle" },
  margin: { left: 30, right: 30 },
};

export default function TestingAttendeesPrintButton({ event, attendees = [] }) {
  // ---- Summaries (same logic as TestingAttendeesList) ----
  // next degree label counts (degree+1)
  const nextLabelCounts = useMemo(() => {
    const map = new Map();
    (attendees || []).forEach((r) => {
      const nextIdx = (Number(r.degree) || 0) + 1;
      const next = gradArray[nextIdx]?.label;
      if (!next) return;
      map.set(next, (map.get(next) || 0) + 1);
    });
    return map;
  }, [attendees]);

  const allLabels = useMemo(() => gradArray.map((g) => g.label), []);
  const kupLabels = useMemo(
    () =>
      allLabels.filter(
        (lab) => /Kup/.test(lab) && !/^10\s*\.?\s*Kup/i.test(lab)
      ),
    [allLabels]
  );
  const danLabels = useMemo(
    () =>
      allLabels.filter(
        (lab) => /Poom/i.test(lab) || /^\s*[1-6]\s*\.?\s*Dan/i.test(lab)
      ),
    [allLabels]
  );

  const summaryRows = useMemo(() => {
    const maxLen = Math.max(kupLabels.length, danLabels.length);
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const kl = kupLabels[i] || "";
      const kc = kl ? nextLabelCounts.get(kl) || 0 : "";
      const dl = danLabels[i] || "";
      const dc = dl ? nextLabelCounts.get(dl) || 0 : "";
      rows.push({ kl, kc, dl, dc });
    }
    return rows;
  }, [kupLabels, danLabels, nextLabelCounts]);

  // Age groups by memberType (Kind, Jugend, Erwachsen)
  const ageGroupCounts = useMemo(() => {
    const res = { Kind: 0, Jugend: 0, Erwachsen: 0 };
    (attendees || []).forEach((r) => {
      const t = r?.raw?.memberType ?? r?.memberType;
      if (t === "Kind") res.Kind += 1;
      else if (t === "Jugend") res.Jugend += 1;
      else if (t === "Erwachsen") res.Erwachsen += 1;
    });
    return res;
  }, [attendees]);

  // Boards summary
  const boardsSummary = useMemo(() => {
    const result = { kinder: 0, damen: 0, herren: 0 };

    const boardsNeededForNext = (label) => {
      if (!label) return 0;
      if (/Poom/i.test(label) || /Dan/i.test(label)) return 2;
      if (/Kup/i.test(label)) {
        const n = parseInt(label, 10);
        if (isNaN(n)) return 0;
        // 9..5 Kup -> 1 board; 4..1 Kup -> 2 boards
        return n >= 5 ? 1 : 2;
      }
      return 0;
    };

    (attendees || []).forEach((r) => {
      const nextIdx = (Number(r.degree) || 0) + 1;
      const nextLabel = gradArray[nextIdx]?.label;
      const boards = boardsNeededForNext(nextLabel);
      if (boards <= 0) return;

      const t = r?.raw?.memberType ?? r?.memberType;
      const title = (r?.raw?.title ?? r?.title ?? "").trim();

      if (t === "Kind") {
        result.kinder += boards;
      } else if (t === "Jugend") {
        result.damen += boards; // Teenagers grouped with Damen
      } else if (t === "Erwachsen") {
        if (title === "Frau") result.damen += boards;
        else result.herren += boards;
      }
    });

    return {
      ...result,
      total: result.kinder + result.damen + result.herren,
    };
  }, [attendees]);

  const handlePrintPdf = () => {
    if (!event || !attendees?.length) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const title = event?.eventName ? String(event.eventName) : "Prüfung";
    const dateStr = event?.eventDateBegin
      ? dayjs(event.eventDateBegin).format("DD.MM.YYYY HH:mm")
      : "";
    const location = event?.locationName ? ` · ${event.locationName}` : "";
    const header = `${title}${location}${dateStr ? " · " + dateStr : ""}`;

    // Header
    doc.setFontSize(14);
    doc.text(header, 40, 40);
    doc.setFontSize(10);
    doc.text(`Teilnehmer: ${attendees.length}`, 40, 58);

    // 1) Attendees table
    const headAtt = [
      [
        "#",
        "Vorname",
        "Nachname",
        "Mitgliedsnummer",
        "Standort",
        "Grad",
        "Alter",
        "Kommentar",
      ],
    ];
    const bodyAtt = attendees.map((r, i) => [
      i + 1,
      r.firstName || "",
      r.lastName || "",
      r.memberNumber || "",
      r.standort || "",
      gradLabel(r.degree),
      r.age ?? "",
      "",
    ]);

    autoTable(doc, {
      startY: 72,
      head: headAtt,
      body: bodyAtt,
      ...tableStyle,
    });

    // Utility to place a titled table after the previous one
    const addTitledTable = (titleText, head, body) => {
      const startY = (doc.lastAutoTable && doc.lastAutoTable.finalY + 18) || 72;
      doc.setFontSize(12);
      doc.text(titleText, 40, startY);
      autoTable(doc, {
        startY: startY + 8,
        head,
        body,
        ...tableStyle,
      });
    };

    // 2) Teilnehmer nach Altersgruppe
    const headAges = [["Kinder", "Jugendliche", "Erwachsene", "Gesamt"]];
    const totalAge =
      ageGroupCounts.Kind + ageGroupCounts.Jugend + ageGroupCounts.Erwachsen;
    const bodyAges = [
      [
        ageGroupCounts.Kind,
        ageGroupCounts.Jugend,
        ageGroupCounts.Erwachsen,
        totalAge,
      ],
    ];
    addTitledTable("Teilnehmer nach Altersgruppe", headAges, bodyAges);

    // 3) Gürtelbedarf (nächster Grad)
    const headBelts = [["Kup", "Anzahl", "Poom / Dan", "Anzahl"]];
    const bodyBelts = summaryRows.map((r) => [
      r.kl || "",
      r.kc !== "" ? r.kc : "",
      r.dl || "",
      r.dc !== "" ? r.dc : "",
    ]);
    addTitledTable("Gürtelbedarf (nächster Grad)", headBelts, bodyBelts);

    // 4) Bretterbedarf
    const headBoards = [
      ["Kinderbretter", "Damenbretter", "Herrenbretter", "Gesamt"],
    ];
    const bodyBoards = [
      [
        boardsSummary.kinder,
        boardsSummary.damen,
        boardsSummary.herren,
        boardsSummary.total,
      ],
    ];
    addTitledTable("Bretterbedarf", headBoards, bodyBoards);

    // Footer page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.getWidth();
      doc.setFontSize(9);
      doc.text(`Seite ${i}`, pageWidth - 60, pageSize.getHeight() - 20);
    }

    doc.save(
      `${title.replace(/\s+/g, "_")}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`
    );
  };

  const isDisabled = !event || !(attendees?.length > 0);

  return (
    <Button
      variant="contained"
      color="info"
      onClick={handlePrintPdf}
      disabled={isDisabled}
      startIcon={<Icon icon="mdi:printer" />}
    >
      Drucken
    </Button>
  );
}
